import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import logger from '../lib/logger';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, ComposedChart, Cell
} from 'recharts';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, DollarSign, TrendingUp, Award, ArrowUpRight, ArrowDownRight, BarChart3, Target, Layers, AlertTriangle, Search, GitCompare, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { useDeepLinkOrg } from '../hooks/useDeepLinkOrg';
import { TileInfoButton } from '../components/common/TileInfoButton';
import { useQuarterDelta } from '../hooks/useQuarterDelta';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import ChainPositionStrip from '../components/layout/ChainPositionStrip';
import { getSectionTheme, getLightSectionTheme } from '../lib/sectionThemes';
import { useTheme } from '../context/ThemeContext';
import PageSubNav from '../components/layout/PageSubNav';
import RoiLeanOverview, { RoiDeepDive } from '../components/roi/RoiLeanOverview';
import RoiCohortPrograms from '../components/roi/RoiCohortPrograms';
import RoiPerformanceValue from '../components/roi/RoiPerformanceValue';
import EngineWeakLinkCallout from '../components/engines/EngineWeakLinkCallout';
import EngineActionPlanCard from '../components/engines/EngineActionPlanCard';
import CollapsibleSection from '../components/layout/CollapsibleSection';
import PageToolbar from '../components/layout/PageToolbar';
import FilterSummaryChip from '../components/layout/FilterSummaryChip';

const ROI_SUBNAV = [
  { id: 'overview', label: 'Overview', to: '/roi-health', end: true },
  { id: 'priorities', label: 'Priorities', to: '/roi-health/priorities' },
  { id: 'deep-dives', label: 'Cohort & Programs', to: '/roi-health/deep-dives' },
  { id: 'cases', label: 'Business Cases', to: '/roi-health/cases' },
  { id: 'trends', label: 'Trends', to: '/roi-health/trends' },
  { id: 'performance-value', label: 'Performance Value', to: '/roi-health/performance-value' },
];

function formatCurrency(val) {
  const n = Number(val) || 0;
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function roiColor(multiple) {
  if (multiple >= 5) return { bg: 'bg-emerald-500', text: 'text-emerald-700', light: 'bg-emerald-50', border: 'border-emerald-200', label: 'Excellent' };
  if (multiple >= 3) return { bg: 'bg-teal-500', text: 'text-teal-700', light: 'bg-teal-50', border: 'border-teal-200', label: 'Strong' };
  if (multiple >= 1) return { bg: 'bg-amber-500', text: 'text-amber-700', light: 'bg-amber-50', border: 'border-amber-200', label: 'Positive' };
  return { bg: 'bg-rose-500', text: 'text-rose-700', light: 'bg-rose-50', border: 'border-rose-200', label: 'Negative' };
}

const CHART_COLORS = ['#06b6d4', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1'];

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const fullName = payload[0]?.payload?.fullName;
  return (
    <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200 text-sm max-w-xs">
      {fullName && <p className="font-semibold text-slate-800 mb-0.5 leading-tight">{fullName}</p>}
      <p className="text-xs text-slate-400 mb-1.5">{label}</p>
      {payload.map((entry, i) => (
        <div key={entry.name || `tt-${i}`} className="flex justify-between gap-4">
          <span className="text-slate-500">{entry.name}:</span>
          <span className="font-semibold" style={{ color: entry.color }}>
            {entry.name?.includes('ROI') ? `${entry.value}x` : formatCurrency(entry.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function ROIHealthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme !== 'light';
  const segs = location.pathname.split('/').filter(Boolean);
  const subView = segs[1] || 'overview';
  const scenarioId = subView === 'deep-dives' ? (segs[2] ? decodeURIComponent(segs[2]) : null) : null;
  const { viewAsOrg, rbac } = useAuth();
  // Deep-link: ?organization_id= scopes the page and (for super admins)
  // aligns the global "Previewing as" banner. Backend guards tenant access.
  const urlOrgId = useDeepLinkOrg();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('overview');
  const [caseSearch, setCaseSearch] = useState('');
  const [caseCompareSel, setCaseCompareSel] = useState([]);

  const toggleCaseCompare = (id) => {
    setCaseCompareSel((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 4) return [...prev.slice(1), id];
      return [...prev, id];
    });
  };

  const initialOrg = urlOrgId || viewAsOrg?.id || '';
  const [selectedOrg, setSelectedOrg] = useState(initialOrg);
  const [organizations, setOrganizations] = useState([]);
  const [selectedCohort, setSelectedCohort] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  // Org-wide value-at-stake — shared with LEI / Maturity / PE / ROE
  // so the dollar figure on the ROI weak-link callout reconciles
  // with every other engine.
  const [valueAtStake, setValueAtStake] = useState(null);
  const [perfValue, setPerfValue] = useState(null);
  useEffect(() => {
    const q = selectedOrg ? `?organization_id=${encodeURIComponent(selectedOrg)}` : '';
    api.get(`/performance-health/value${q}`).then((r) => setPerfValue(r.data)).catch(() => setPerfValue(null));
  }, [selectedOrg]);
  const [orgCohorts, setOrgCohorts] = useState([]);
  const roiQuarterDelta = useQuarterDelta('roi');

  // All org cohorts — for the inline "Assign cohort" control on the
  // Business Cases list (tags scenarios so the Cohort & Programs view fills in).
  useEffect(() => {
    if (!selectedOrg) { setOrgCohorts([]); return; }
    api.get(`/people-engine/cohorts?organization_id=${encodeURIComponent(selectedOrg)}`)
      .then((r) => setOrgCohorts(r.data?.cohorts || []))
      .catch(() => setOrgCohorts([]));
  }, [selectedOrg]);

  const assignCohort = async (scenarioId, cohortId) => {
    try {
      await api.patch(`/scenarios/${scenarioId}`, { cohort_id: cohortId || null });
      loadData();
    } catch (err) {
      logger.error('assign cohort failed:', err);
      toast.error('Could not assign cohort.');
    }
  };

  // Fetch orgs
  useEffect(() => {
    api.get('/people-engine/organizations').then(r => {
      setOrganizations(r.data?.organizations || []);
      if (!selectedOrg && r.data?.organizations?.length > 0) {
        setSelectedOrg(r.data.organizations[0].id);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch ROI health data
  const loadData = useCallback(() => {
    if (!selectedOrg) return;
    setLoading(true);
    const params = new URLSearchParams({ organization_id: selectedOrg });
    if (selectedCohort) params.set('cohort_id', selectedCohort);
    if (selectedProgram) params.set('program_name', selectedProgram);
    api.get(`/scenarios/roi-health?${params}`)
      .then(r => setData(r.data))
      .catch(err => { logger.error('ROI health fetch failed:', err); setData(null); })
      .finally(() => setLoading(false));
  }, [selectedOrg, selectedCohort, selectedProgram]);

  useEffect(() => { loadData(); }, [loadData]);

  // Honour ?program= / ?cohort= deep-links from the Cohort & Programs
  // leaderboard so "View cases" opens pre-filtered.
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const p = sp.get('program');
    const c = sp.get('cohort');
    if (p) setSelectedProgram(p);
    if (c) setSelectedCohort(c);
  }, [location.search]);

  // Fetch value-at-stake for dollar framing on the weak-link callout.
  useEffect(() => {
    if (!selectedOrg) { setValueAtStake(null); return; }
    let cancelled = false;
    api.get(`/lei-health/value-at-stake?organization_id=${encodeURIComponent(selectedOrg)}`)
      .then((res) => {
        if (!cancelled && res?.data?.annual_value_at_stake_usd != null) {
          setValueAtStake(res.data);
        }
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [selectedOrg]);

  const summary = data?.summary || {};
  const scenarios = data?.scenarios || [];
  const filters = data?.filters || { cohorts: [], programs: [] };

  // Chart data — sorted by date
  const chartData = [...scenarios]
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
    .map(s => ({
      name: s.name?.length > 25 ? s.name.slice(0, 25) + '...' : s.name,
      fullName: s.name,
      date: s.date,
      'Net Benefit': s.net_benefit,
      'Investment': s.investment,
      'ROI Multiple': s.roi_multiple,
    }));

  // Waterfall data for investment vs return
  const waterfallData = summary.scenario_count > 0 ? [
    { name: 'Investment', value: summary.total_investment, fill: '#6366f1' },
    { name: 'Financial Impact', value: summary.total_financial_impact, fill: '#10b981' },
    { name: 'Net Benefit', value: summary.total_net_benefit, fill: '#06b6d4' },
  ] : [];

  const health = roiColor(summary.avg_roi_multiple || 0);

  // Quarter-over-quarter deltas — bucket scenarios by their
  // signing date into ISO calendar quarters (YYYY-Q[1-4]). We
  // keep up to the last 6 quarters of buckets so each hero stat
  // can also feed a hover-reveal sparkline.
  const quarterBuckets = (() => {
    if (!scenarios.length) return [];
    const buckets = new Map();
    for (const s of scenarios) {
      if (!s.date) continue;
      const d = new Date(s.date);
      if (Number.isNaN(d.getTime())) continue;
      const q = Math.floor(d.getMonth() / 3) + 1;
      const key = `${d.getFullYear()}-Q${q}`;
      const b = buckets.get(key) || { key, year: d.getFullYear(), q, scenarios: [] };
      b.scenarios.push(s);
      buckets.set(key, b);
    }
    const sorted = [...buckets.values()].sort((a, b) =>
      a.year !== b.year ? a.year - b.year : a.q - b.q
    );
    const aggregate = (rows) => {
      const inv = rows.reduce((acc, r) => acc + (Number(r.investment) || 0), 0);
      const nb = rows.reduce((acc, r) => acc + (Number(r.net_benefit) || 0), 0);
      const fi = rows.reduce((acc, r) => acc + (Number(r.financial_impact) || (Number(r.investment) || 0) + (Number(r.net_benefit) || 0)), 0);
      const bestRoi = rows.reduce((acc, r) => Math.max(acc, Number(r.roi_multiple) || 0), 0);
      return {
        inv,
        nb,
        bestRoi,
        avgRoi: inv > 0 ? +(fi / inv).toFixed(2) : 0,
      };
    };
    return sorted.slice(-6).map((b) => ({ ...b, ...aggregate(b.scenarios) }));
  })();

  const qoqDeltas = (() => {
    if (quarterBuckets.length < 2) return null;
    const current = quarterBuckets[quarterBuckets.length - 1];
    const prev = quarterBuckets[quarterBuckets.length - 2];
    const pct = (cur, prv) => (prv > 0 ? ((cur - prv) / prv) * 100 : null);
    return {
      label: prev.key,
      avgRoiDelta: +(current.avgRoi - prev.avgRoi).toFixed(2),
      netBenefitDelta: pct(current.nb, prev.nb),
      investmentDelta: pct(current.inv, prev.inv),
    };
  })();

  // Sparkline series — only render when ≥2 quarters are
  // available (otherwise a single dot is just visual noise).
  const sparkAvgRoi = quarterBuckets.length >= 2
    ? quarterBuckets.map((b) => ({ label: b.key, value: b.avgRoi }))
    : null;
  const sparkNetBenefit = quarterBuckets.length >= 2
    ? quarterBuckets.map((b) => ({ label: b.key, value: Math.round(b.nb) }))
    : null;
  const sparkInvestment = quarterBuckets.length >= 2
    ? quarterBuckets.map((b) => ({ label: b.key, value: Math.round(b.inv) }))
    : null;
  const sparkBest = quarterBuckets.length >= 2
    ? quarterBuckets.map((b) => ({ label: b.key, value: b.bestRoi }))
    : null;

  const __pageTheme = isDark
    ? { ...getSectionTheme('ROI'), heroGradient: 'bg-[#0a0f1c]', bodyTint: 'bg-[#0a0f1c]', textBase: 'text-white', textLighter: 'text-slate-400', titleClass: 'text-slate-50 font-extrabold' }
    : getLightSectionTheme('ROI');
  // North-star tile — single CFO-grade headline metric. Promotes
  // Average ROI from a small stat into the giant centerpiece, with
  // a verbal description of the health band, the QoQ delta arrow,
  // and the 6Q sparkline as a peer-comparison surface. Only render
  // when there are scenarios — empty pages stay clean.
  const roiHeroNorthStar = (scenarios.length > 0 && summary) ? {
    label: 'Average ROI · north-star metric',
    value: `${summary.avg_roi_multiple || 0}`,
    valueSuffix: 'x',
    description: (
      health.label
        ? `${health.label} · ${summary.scenario_count} scenario${summary.scenario_count !== 1 ? 's' : ''} contributing`
        : `${summary.scenario_count} scenarios contributing`
    ),
    percentile: typeof summary.peer_percentile === 'number' ? summary.peer_percentile : undefined,
    percentileLabel: summary.peer_benchmark?.industry_name
      ? `${summary.peer_benchmark.industry_name} percentile`
      : 'L&D peer percentile',
    peerLabel: 'Peer median',
    peerValue: summary.peer_median_roi ? `${summary.peer_median_roi}x` : undefined,
    sparklineData: sparkAvgRoi,
    sparklineFormatter: (v) => `${v}x`,
    testId: 'roi-hero-northstar',
    ...(roiQuarterDelta ? {
      delta: roiQuarterDelta.delta,
      deltaText: roiQuarterDelta.deltaText,
      deltaLabel: roiQuarterDelta.deltaLabel,
    } : (qoqDeltas && qoqDeltas.avgRoiDelta !== 0 ? {
      delta: qoqDeltas.avgRoiDelta,
      deltaText: `${qoqDeltas.avgRoiDelta > 0 ? '+' : ''}${qoqDeltas.avgRoiDelta}x`,
      deltaLabel: 'vs last quarter',
    } : {})),
    whyPanel: {
      label: 'Average ROI',
      value: `${summary.avg_roi_multiple || 0}`,
      valueSuffix: 'x',
      description: (
        `Mean of (annual_financial_impact ÷ total_investment) across ${summary.scenario_count} active scenario${summary.scenario_count !== 1 ? 's' : ''}. ROI multiples below 1.0x indicate the program isn't yet covering its costs; multiples above 2.0x are typically considered "healthy" for L&D.`
      ),
      formula: 'Σ (annual_financial_impact ÷ total_investment) ÷ scenario_count',
      breakdown: [
        { label: 'Total Net Benefit', value: formatCurrency(summary.total_net_benefit || 0), hint: 'sum across scenarios' },
        { label: 'Total Investment', value: formatCurrency(summary.total_investment || 0), hint: 'sum across scenarios' },
        { label: 'Best Performing', value: `${summary.best_roi_multiple || 0}x`, hint: summary.best_scenario || 'top scenario' },
        { label: 'Scenarios Contributing', value: summary.scenario_count, hint: 'with computable ROI' },
      ],
      history: Array.isArray(quarterBuckets) && quarterBuckets.length
        ? quarterBuckets.slice(-4).map((b) => ({ label: b.key, value: `${b.avgRoi}x` }))
        : null,
      links: [
        { label: 'Open Saved Scenarios', path: '/scenarios' },
        { label: 'Build a new Business Case', path: '/business-case-builder' },
        { label: 'Compare two scenarios', path: '/comparison-analytics' },
      ],
    },
  } : null;
  // Page-scoped narrative band — tells the user the single
  // biggest QoQ mover good and bad in one glance, mirrored from
  // the home-page WhatChangedTile pattern. Always renders when
  // scenarios exist so the band gives a steady-state read-out
  // even on tenants with only one quarter of data.
  const roiHeroNarrative = (scenarios.length > 0 && summary) ? (() => {
    const candidates = qoqDeltas ? [
      qoqDeltas.avgRoiDelta !== 0
        ? { label: 'Average ROI', delta: qoqDeltas.avgRoiDelta, deltaText: `${qoqDeltas.avgRoiDelta > 0 ? '+' : ''}${qoqDeltas.avgRoiDelta}x` }
        : null,
      qoqDeltas.netBenefitDelta !== null && qoqDeltas.netBenefitDelta !== 0
        ? { label: 'Net Benefit', delta: qoqDeltas.netBenefitDelta, deltaText: `${qoqDeltas.netBenefitDelta > 0 ? '+' : ''}${Math.round(qoqDeltas.netBenefitDelta)}%` }
        : null,
      qoqDeltas.investmentDelta !== null && qoqDeltas.investmentDelta !== 0
        ? { label: 'Investment', delta: -qoqDeltas.investmentDelta /* inverted: less spend = good */, deltaText: `${qoqDeltas.investmentDelta > 0 ? '+' : ''}${Math.round(qoqDeltas.investmentDelta)}%` }
        : null,
    ].filter(Boolean) : [];
    if (!candidates.length) {
      // Steady-state fallback so the recipient still sees a
      // narrative band on tenants without a QoQ delta yet.
      return {
        headline: `Steady at ${summary.avg_roi_multiple || 0}x ROI · ${summary.scenario_count} scenario${summary.scenario_count !== 1 ? 's' : ''} contributing`,
      };
    }
    const sortedDesc = [...candidates].sort((a, b) => b.delta - a.delta);
    const gainer = sortedDesc[0]?.delta > 0 ? sortedDesc[0] : null;
    const decliner = sortedDesc[sortedDesc.length - 1]?.delta < 0 ? sortedDesc[sortedDesc.length - 1] : null;
    if (!gainer && !decliner) return {
      headline: `Steady at ${summary.avg_roi_multiple || 0}x ROI · ${summary.scenario_count} scenario${summary.scenario_count !== 1 ? 's' : ''} contributing`,
    };
    return { gainer, decliner };
  })() : null;
  const roiHeroActions = (
    <>
      <PageToolbar theme={__pageTheme} collapsePrefix="roi" organizationId={selectedOrg} testId="roi-toolbar" />
      <Select value={selectedOrg} onValueChange={v => { setSelectedOrg(v); setSelectedCohort(''); setSelectedProgram(''); }}>
        <SelectTrigger className="w-[220px] bg-white border-slate-200 text-slate-700" data-testid="roi-org-select">
          <SelectValue placeholder="Select organization" />
        </SelectTrigger>
        <SelectContent>
          {organizations.map(o => (
            <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  );
  // Hero stat tiles — lifted out of the body so the hero carries
  // the headline ROI numbers (parity with Maturity / LEI / ROE
  // Health). Only render when the org actually has scenarios;
  // otherwise leave stats empty so the hero stays clean above the
  // "No ROI Data" empty state.
  // Drop Average ROI from the stats grid since it's now the
  // north-star centerpiece — keeps the hero from showing the
  // same number twice. Each remaining stat now ships a
  // `whyPanel` so a single click drills into the breakdown.
  const roiHeroStats = scenarios.length > 0 && summary ? [
    {
      label: 'Total Net Benefit',
      value: formatCurrency(summary.total_net_benefit || 0),
      hint: `across ${summary.scenario_count} scenario${summary.scenario_count !== 1 ? 's' : ''}`,
      testId: 'roi-hero-stat-net-benefit',
      sparkline: sparkNetBenefit,
      sparklineFormatter: (v) => formatCurrency(v),
      ...(qoqDeltas && qoqDeltas.netBenefitDelta !== null ? {
        delta: Math.round(qoqDeltas.netBenefitDelta),
        deltaText: `${qoqDeltas.netBenefitDelta > 0 ? '+' : ''}${Math.round(qoqDeltas.netBenefitDelta)}%`,
        deltaLabel: 'vs last quarter',
      } : {}),
      whyPanel: {
        label: 'Total Net Benefit',
        value: formatCurrency(summary.total_net_benefit || 0),
        description: 'Sum of every active scenario\'s annual financial impact, less its total investment. Shows the absolute dollar value the L&D portfolio is generating this quarter.',
        formula: 'Σ (annual_financial_impact - total_investment)',
        history: Array.isArray(quarterBuckets) && quarterBuckets.length
          ? quarterBuckets.slice(-4).map(b => ({ label: b.key, value: formatCurrency(b.nb) }))
          : null,
        links: [
          { label: 'See contributing scenarios', path: '/scenarios' },
          { label: 'Build another business case', path: '/business-case-builder' },
        ],
      },
    },
    {
      label: 'Total Investment',
      value: formatCurrency(summary.total_investment || 0),
      hint: 'learning spend',
      testId: 'roi-hero-stat-investment',
      sparkline: sparkInvestment,
      sparklineFormatter: (v) => formatCurrency(v),
      ...(qoqDeltas && qoqDeltas.investmentDelta !== null ? {
        delta: Math.round(qoqDeltas.investmentDelta),
        deltaText: `${qoqDeltas.investmentDelta > 0 ? '+' : ''}${Math.round(qoqDeltas.investmentDelta)}%`,
        deltaLabel: 'vs last quarter',
        deltaInverted: true,
      } : {}),
      whyPanel: {
        label: 'Total Investment',
        value: formatCurrency(summary.total_investment || 0),
        description: 'Aggregate L&D programme spend across all active scenarios. Includes vendor fees, internal time, content licensing, and platform costs as captured in each scenario\'s investment breakdown.',
        formula: 'Σ scenario.total_investment',
        history: Array.isArray(quarterBuckets) && quarterBuckets.length
          ? quarterBuckets.slice(-4).map(b => ({ label: b.key, value: formatCurrency(b.inv) }))
          : null,
        links: [
          { label: 'Review investment breakdowns', path: '/scenarios' },
        ],
      },
    },
    {
      label: 'Best Performing',
      value: `${summary.best_roi_multiple || 0}x`,
      hint: summary.best_scenario || '—',
      testId: 'roi-hero-stat-best',
      sparkline: sparkBest,
      sparklineFormatter: (v) => `${v}x`,
      whyPanel: {
        label: 'Best Performing Scenario',
        value: `${summary.best_roi_multiple || 0}x`,
        description: `${summary.best_scenario || 'Top scenario'} is generating the highest ROI multiple in the portfolio. The bar to beat — analysing what makes this scenario succeed often surfaces patterns to replicate elsewhere.`,
        history: Array.isArray(quarterBuckets) && quarterBuckets.length
          ? quarterBuckets.slice(-4).map(b => ({ label: b.key, value: `${b.bestRoi}x` }))
          : null,
        links: [
          { label: 'Open Saved Scenarios', path: '/scenarios' },
          { label: 'Compare to another scenario', path: '/comparison-analytics' },
        ],
      },
    },
  ] : null;
  return (
    <PageShell
      theme={__pageTheme}
      stickyTitle="Financial Health"
      hero={(
      <PageHero
        title="Financial Health"
        titleHint="ROI"
        description="Financial return on learning investment (ROI) across all business cases — the FINANCIAL IMPACT stage of the Impact Chain. Track how learning programmes translate into measurable business value."
        theme={__pageTheme}
        icon={DollarSign}
        actions={roiHeroActions}
        northStar={undefined}
        narrative={undefined}
        stats={undefined}
        pinSnapshot={isDark && scenarios.length > 0 ? [
          { label: 'Average ROI', value: `${summary.avg_roi_multiple || 0}x`, hint: health.label },
          { label: 'Total Net Benefit', value: formatCurrency(summary.total_net_benefit || 0) },
          { label: 'Best Performing', value: `${summary.best_roi_multiple || 0}x` },
          ...(perfValue?.has_data ? [{ label: 'Performance Value', value: formatCurrency(perfValue.summary.attributable || 0), hint: `${perfValue.summary.priced_kpis}/${perfValue.summary.total_kpis} KPIs priced` }] : []),
        ] : undefined}
        testId="roi-health-hero"
      >
        {/* Light hero accents — matches LIE home / LEI / PE / ROE. */}
        <div
          className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-teal-400 via-teal-500 to-teal-400 pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 50%, #6366f1 0%, transparent 50%), ' +
              'radial-gradient(circle at 80% 50%, #14b8a6 0%, transparent 50%)',
          }}
          aria-hidden="true"
        />
      </PageHero>
    )}>
    <div className="space-y-6" data-testid="roi-health-page">

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
        </div>
      ) : (
        <div className={cn('space-y-6 min-w-0', isDark ? 'home-dark-scope' : 'mc-light')}>
          <PageSubNav items={ROI_SUBNAV} />
          {!scenarios.length && subView === 'overview' ? (
            <Card className="border-dashed border-2 border-slate-200">
              <CardContent className="py-16 text-center">
                <DollarSign className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <h3 className="text-lg font-semibold text-slate-700">No ROI Data</h3>
                <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                  No business cases with ROI calculations found for this organization. Create scenarios in the Business Case Builder to see ROI data here.
                </p>
              </CardContent>
            </Card>
          ) : (
          <>

          {/* Overview — Lean dark KPI overview + scenario breakdown + trend + alerts + action plan */}
          {subView === 'overview' && (
            <RoiLeanOverview
              summary={summary}
              scenarios={scenarios}
              chartData={chartData}
              selectedOrg={selectedOrg}
              valueAtStake={valueAtStake}
              perfValue={perfValue}
              navigate={navigate}
            />
          )}

          {/* Performance Value — attributable KPI movement from Performance Health, priced with approved value units */}
          {subView === 'performance-value' && <RoiPerformanceValue selectedOrg={selectedOrg} orgName={organizations.find((o) => o.id === selectedOrg)?.name || viewAsOrg?.name || rbac?.organization_name || ''} />}

          {/* Priorities — dedicated Risk Areas & Action Plan page */}
          {subView === 'priorities' && (() => {
            const ranked = [...scenarios].filter((s) => typeof s.roi_multiple === 'number').sort((a, b) => a.roi_multiple - b.roi_multiple);
            const weakest = ranked[0] || null;
            return (
              <div className="space-y-4" data-testid="roi-priority-focus-section">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Risk Areas &amp; Action Plan</h2>
                  <p className="text-sm text-slate-500">Biggest risk to close + your recommended action plan</p>
                </div>
                {weakest ? (
                  <div className="space-y-3">
                    <EngineWeakLinkCallout
                      entityLabel={weakest.name || 'lowest-ROI scenario'}
                      score={weakest.roi_multiple}
                      overall={summary.avg_roi_multiple}
                      metricSuffix="x"
                      description="is your lowest-ROI program right now."
                      testIdPrefix="roi-weak-link"
                    />
                    <EngineActionPlanCard engine="roi" params={{ scenario_id: weakest.id, organization_id: selectedOrg, score: weakest.roi_multiple, overall_score: summary.avg_roi_multiple }} />
                  </div>
                ) : (
                  <div className="text-center py-12" data-testid="roi-priorities-empty">
                    <AlertTriangle className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">No Priorities Yet</h3>
                    <p className="text-sm text-slate-500 max-w-md mx-auto">Add business cases with ROI results to surface your lowest-ROI program and a recommended action plan here.</p>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Cohort & Programs — financial health (ROI) per cohort/program.
              Backward-compat: a /deep-dives/:scenarioId link still opens the
              per-business-case detail. */}
          {subView === 'deep-dives' && (scenarioId
            ? <RoiDeepDive scenarios={scenarios} summary={summary} scenarioId={scenarioId} selectedOrg={selectedOrg} navigate={navigate} />
            : <RoiCohortPrograms scenarios={scenarios} filters={filters} summary={summary} selectedOrg={selectedOrg} navigate={navigate} />)}

          {/* Filters — shared by Business Cases + Trends segments */}
          {(subView === 'cases' || subView === 'trends') && (filters.cohorts.length > 0 || filters.programs.length > 1) && (
            <div className="flex gap-3 flex-wrap" data-testid="roi-deepdive-controls">
              {filters.cohorts.length > 0 && (
                <Select value={selectedCohort || '_all'} onValueChange={v => setSelectedCohort(v === '_all' ? '' : v)}>
                  <SelectTrigger className="w-[200px]" data-testid="roi-cohort-filter">
                    <SelectValue placeholder="All Cohorts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Cohorts</SelectItem>
                    {filters.cohorts.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {filters.programs.length > 1 && (
                <Select value={selectedProgram || '_all'} onValueChange={v => setSelectedProgram(v === '_all' ? '' : v)}>
                  <SelectTrigger className="w-[260px]" data-testid="roi-program-filter">
                    <SelectValue placeholder="All Programs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Programs</SelectItem>
                    {filters.programs.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* Trends — ROI over time + Investment vs Return + Top performers */}
          {subView === 'trends' && (
            <div className="space-y-6">
              {chartData.length > 1 && (
                <Card>
                  <CardContent className="p-5">
                    <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
                      ROI by Business Case Over Time
                      <TileInfoButton
                        testid="roi-time-info"
                        title="ROI by Business Case Over Time"
                        body="A composed chart: blue bars = Investment per case (left axis, $), green bars = Net Benefit (left axis, $), and the gold line = ROI Multiple (right axis, x). When the gold line dips below 1.0× any bar pair, that case lost money. When the line rises while bars grow, you're scaling profitable wins. Hover any column for the exact figures."
                      />
                    </h3>
                    <ResponsiveContainer width="100%" height={320}>
                      <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" />
                        <YAxis yAxisId="money" tick={{ fontSize: 11 }} tickFormatter={v => formatCurrency(v)} />
                        <YAxis yAxisId="roi" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => `${v}x`} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar yAxisId="money" dataKey="Investment" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={28} opacity={0.7} />
                        <Bar yAxisId="money" dataKey="Net Benefit" fill="#10b981" radius={[4, 4, 0, 0]} barSize={28} />
                        <Line yAxisId="roi" type="monotone" dataKey="ROI Multiple" stroke="#f59e0b" strokeWidth={3} dot={{ r: 5, fill: '#f59e0b' }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardContent className="p-5">
                  <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
                    Investment vs Return
                    <TileInfoButton
                      testid="roi-waterfall-info"
                      title="Investment vs Return"
                      body="A simple two-bar reconciliation: total dollars in (Investment) vs total dollars out (Return). Read it left-to-right — anything left to the right of the Investment bar is your Net Benefit. The taller the gap, the more value L&D delivered above its spend. If the bars are similar height, the program is breaking even; if Return is shorter, you're losing money this period."
                    />
                  </h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={waterfallData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => formatCurrency(v)} />
                      <Tooltip formatter={v => formatCurrency(v)} cursor={{ fill: isDark ? 'rgba(148,163,184,0.12)' : 'rgba(0,0,0,0.04)' }} contentStyle={isDark ? { background: '#0b1220', border: '1px solid #22304d', borderRadius: 8, color: '#e2e8f0' } : undefined} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={60}>
                        {waterfallData.map((entry, i) => (
                          <Cell key={entry.name} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <div>
                <h3 className="text-sm font-bold text-slate-800 mb-3">Top Performing Business Cases</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[...scenarios].sort((a, b) => b.roi_percent - a.roi_percent).slice(0, 3).map((s, i) => {
                    const rc = roiColor(s.roi_multiple);
                    return (
                      <Card
                        key={s.id || `top-${i}`}
                        className={cn("cursor-pointer hover:shadow-md transition-shadow border-2", s.is_best ? 'border-amber-300 bg-amber-50/30' : 'border-slate-100')}
                        onClick={() => navigate(`/scenarios?highlight=${encodeURIComponent(s.id)}`)}
                        data-testid={`roi-top-scenario-${i}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-slate-800 truncate">{s.name}</p>
                              <p className="text-xs text-slate-400">{s.date}</p>
                            </div>
                            {s.is_best && <Badge className="bg-amber-100 text-amber-700 border-amber-200 flex-shrink-0 ml-2"><Award className="h-3 w-3 mr-1" />Best</Badge>}
                          </div>
                          <div className="flex items-end justify-between mt-3">
                            <div>
                              <p className="text-xs text-slate-500">Net Benefit</p>
                              <p className="text-lg font-bold text-emerald-700">{formatCurrency(s.net_benefit)}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-slate-500">ROI</p>
                              <p className={cn("text-2xl font-extrabold", rc.text)}>{s.roi_multiple}x</p>
                            </div>
                          </div>
                          {s.kpis_impacted?.length > 0 && (
                            <div className="flex gap-1 mt-2 flex-wrap">
                              {s.kpis_impacted.map((k, ki) => (
                                <Badge key={`${k}-${ki}`} variant="outline" className="text-[9px] bg-slate-50">{k}</Badge>
                              ))}
                            </div>
                          )}
                          <div className="mt-3 pt-2 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs text-indigo-600 hover:text-indigo-700 px-2"
                              onClick={() => navigate(`/roi-health/deep-dives/${encodeURIComponent(s.id)}`)}
                              data-testid={`roi-top-scenario-action-plan-${i}`}
                            >
                              <Target className="h-3.5 w-3.5 mr-1" /> Action Plan
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Business Cases — full sortable table */}
          {subView === 'cases' && (
            <Card>
              <CardContent className="p-0">
                <div className="flex items-center justify-between gap-3 p-3 border-b border-slate-200">
                  <div className="relative w-full max-w-xs">
                    <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <Input
                      value={caseSearch}
                      onChange={(e) => setCaseSearch(e.target.value)}
                      placeholder="Search business cases…"
                      className="pl-8 h-9 bg-white border-slate-200 text-slate-700"
                      data-testid="roi-cases-search"
                    />
                  </div>
                  {caseCompareSel.length > 0 && (
                    <div className="flex items-center gap-2" data-testid="roi-cases-compare-bar">
                      <span className="text-xs text-indigo-700 font-medium">{caseCompareSel.length}/4 selected</span>
                      <Button variant="ghost" size="sm" className="h-8 text-slate-500" onClick={() => setCaseCompareSel([])} data-testid="roi-cases-compare-clear">
                        <X className="h-3.5 w-3.5 mr-1" /> Clear
                      </Button>
                      <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-500 text-white" disabled={caseCompareSel.length < 2}
                        onClick={() => navigate(`/compare?ids=${caseCompareSel.join(',')}`)} data-testid="roi-cases-compare-go">
                        <GitCompare className="h-3.5 w-3.5 mr-1" /> Compare
                      </Button>
                    </div>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="roi-scenarios-table">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="w-10 p-3"></th>
                        <th className="text-left p-3 font-semibold text-slate-600">Business Case</th>
                        <th className="text-left p-3 font-semibold text-slate-600">Date</th>
                        <th className="text-left p-3 font-semibold text-slate-600">Cohort</th>
                        <th className="text-right p-3 font-semibold text-slate-600">Investment</th>
                        <th className="text-right p-3 font-semibold text-slate-600">Financial Impact</th>
                        <th className="text-right p-3 font-semibold text-slate-600">Net Benefit</th>
                        <th className="text-right p-3 font-semibold text-slate-600">ROI</th>
                        <th className="text-center p-3 font-semibold text-slate-600">KPIs</th>
                        <th className="text-center p-3 font-semibold text-slate-600">Action Plan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const q = caseSearch.trim().toLowerCase();
                        const rows = [...scenarios]
                          .filter((s) => !q || (s.name || '').toLowerCase().includes(q))
                          .sort((a, b) => b.roi_percent - a.roi_percent);
                        if (rows.length === 0) {
                          return (
                            <tr><td colSpan={10} className="p-6 text-center text-slate-500" data-testid="roi-cases-search-empty">No business cases match "{caseSearch}".</td></tr>
                          );
                        }
                        return rows.map((s, i) => {
                        const rc = roiColor(s.roi_multiple);
                        return (
                          <tr
                            key={s.id || `row-${i}`}
                            className={cn("border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors", s.is_best && "bg-amber-50/50")}
                            onClick={() => navigate(`/scenarios?highlight=${encodeURIComponent(s.id)}`)}
                            data-testid={`roi-scenario-table-row-${i}`}
                          >
                            <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={caseCompareSel.includes(s.id)}
                                onChange={() => toggleCaseCompare(s.id)}
                                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer accent-indigo-600"
                                data-testid={`roi-case-compare-check-${i}`}
                              />
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                {s.is_best && <Award className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />}
                                <div className="min-w-0">
                                  <p className="font-medium text-slate-800 truncate max-w-[250px]">{s.name}</p>
                                  <p className="text-xs text-slate-400">{s.analysis_type} | LEI +{s.lei_improvement}%</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-3 text-slate-600">{s.date}</td>
                            <td className="p-3" onClick={(e) => e.stopPropagation()}>
                              <Select value={s.cohort_id || '_none'} onValueChange={(v) => assignCohort(s.id, v === '_none' ? null : v)}>
                                <SelectTrigger className="h-8 w-[168px] text-xs" data-testid={`roi-assign-cohort-${i}`}>
                                  <SelectValue placeholder="Assign cohort" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_none">Unassigned</SelectItem>
                                  {orgCohorts.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-3 text-right font-medium text-indigo-700">{formatCurrency(s.investment)}</td>
                            <td className="p-3 text-right font-medium text-slate-700">{formatCurrency(s.financial_impact)}</td>
                            <td className="p-3 text-right">
                              <span className={cn("font-bold", s.net_benefit >= 0 ? "text-emerald-700" : "text-rose-600")}>
                                {s.net_benefit >= 0 ? <ArrowUpRight className="h-3 w-3 inline mr-0.5" /> : <ArrowDownRight className="h-3 w-3 inline mr-0.5" />}
                                {formatCurrency(Math.abs(s.net_benefit))}
                              </span>
                            </td>
                            <td className="p-3 text-right">
                              <span className={cn("text-lg font-extrabold", rc.text)}>{s.roi_multiple}x</span>
                            </td>
                            <td className="p-3 text-center">
                              <Badge variant="outline" className="text-xs">{s.kpi_count}</Badge>
                            </td>
                            <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                                onClick={() => navigate(`/roi-health/deep-dives/${encodeURIComponent(s.id)}`)}
                                data-testid={`roi-scenario-action-plan-${i}`}
                              >
                                <Target className="h-3.5 w-3.5 mr-1" /> Action Plan
                              </Button>
                            </td>
                          </tr>
                        );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
          </>
          )}
        </div>
      )}
    </div>
    </PageShell>
  );
}
